/* eslint-disable prefer-template */
import { getAPIEndpointURL, getInternalLogHeader } from '../utility/utils'

const BASE_URL = getAPIEndpointURL()+'/api'

/**
 * Convierte el cuerpoDocumento de un DTE ya transmitido al formato del
 * formulario. Los catálogos se reciben por parámetro porque este módulo no
 * tiene acceso al store; si no llegan, se conserva el código del documento
 * original como etiqueta en lugar de inventar un valor.
 */
export function transformMHProductsToLocal(items, catalogs = {}) {
  const { measurementUnits = [], saleTypes = [] } = catalogs

  // Nunca devuelve null: la tabla lee row.unitOfMeasure.label y
  // row.prodType.label sin optional chaining
  const resolverCatalogo = (opciones, codigo) => {
    if (codigo === null || codigo === undefined) return null
    const buscado = String(codigo)
    const buscadoNum = Number(buscado)

    const encontrado = (opciones || []).find((opcion) => {
      const valor = String(opcion.value)
      if (valor === buscado) return true
      // Los catálogos usan códigos con cero a la izquierda ('01', '59') y el
      // DTE los transmite como número (1, 59): hay que comparar también
      // numéricamente o nunca coinciden
      const valorNum = Number(valor)
      return (
        Number.isFinite(valorNum) &&
        Number.isFinite(buscadoNum) &&
        valorNum === buscadoNum
      )
    })

    return encontrado || { value: buscado, label: buscado }
  }

  return (items || []).map((item) => {
    // tipoItem sigue CAT-011: 1 = Bienes, 2 = Servicios
    const esServicio = String(item.tipoItem) === '2'
    const nombreProdType = esServicio ? 'Servicio' : 'Producto'

    return {
      id: item.numItem,
      prodType: { value: nombreProdType, label: nombreProdType },
      productCode: item.codigo,
      productName: item.descripcion,
      quantity: item.cantidad,
      description: item.descripcion,
      // uniMedida y tipoItem vienen del documento original: se resuelven
      // contra su catálogo en vez de asumir "Metro" / "Servicios"
      unitOfMeasure: resolverCatalogo(measurementUnits, item.uniMedida) ?? {
        value: '',
        label: '',
      },
      TypeOfSale: resolverCatalogo(saleTypes, item.tipoItem),
      price: item.precioUni,
      discount: item.montoDescu,
      accept: true,
      isActive: true,
      // Datos fiscales del documento original. La nota los necesita para
      // reproducir los ajustes de IVA por ítem (#99-#101) sin recalcularlos
      original: {
        tipoItem: item.tipoItem ?? null,
        ventaGravada: item.ventaGravada ?? 0,
        ventaNoSuj: item.ventaNoSuj ?? 0,
        ventaExenta: item.ventaExenta ?? 0,
        ivaPerci: item.ivaPerci ?? 0,
        ivaRete: item.ivaRete ?? 0,
        noGravado: item.noGravado ?? 0,
        tributos: item.tributos ?? null,
      },
    }
  })
}

export const request = async (url, method, headers, data) => {
  const response = await fetch(url, {
    method,
    headers,
    body: JSON.stringify(data),
  })

  const contentType = response.headers.get('content-type') || ''

  if (contentType.includes('application/json')) {
    const responseText = await response.text()

    if (!responseText) {
      return {}
    }

    try {
      return JSON.parse(responseText)
    } catch (error) {
      return {}
    }
  }

  if (!response.ok) {
    return {}
  }

  try {
    return await response.json()
  } catch (error) {
    return {}
  }
}

export const getTemporalToken = async (user) => {
  const response = await request(
    `${BASE_URL}/auth/login`,
    'POST',
    {
      'Content-Type': 'application/json',
    },
    {
      userName: user?.userName,
      password: user?.password,
    }
  )

  return response?.access_token
}

export const billingLogin = async (user) => {
  const response = await request(
    `${BASE_URL}/auth/login`,
    'POST',
    {
      'Content-Type': 'application/json',
    },
    {
      userName: user?.userName,
      password: user?.password,
    }
  )

  return response
}

export const submitMHTransactionRequest = async (
  data,
  token,
  companyId
) => {
  return await request(
    `${BASE_URL}/transaction/send-mhh/${companyId}`,
    'POST',
    {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    data
  )
}

export const submitMHInvalidateTransactionRequest = async (
  data,
  token,
  companyId
) => {
  return await request(
    `${BASE_URL}/transaction/invalidateDte/${companyId}`,
    'POST',
    {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    data
  )
}

//

export const submitMHContingencyRequest = async (
  data,
  token,
  companyId
) => {
  return await request(
    `${BASE_URL}/transaction/contingency/${companyId}`,
    'POST',
    {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    data
  )
}

export const getCountriesRequest = async (token) => {
  return await request(`${BASE_URL}/catalog/countries`, 'GET', {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  })
}

export const getDepartmentsRequest = async (token) => {
  return await request(`${BASE_URL}/catalog/departments`, 'GET', {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  })
}

export const getCitiesRequest = async (departmentCode, token) => {
  return await request(
    `${BASE_URL}/catalog/cities/${departmentCode}`,
    'GET',
    {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }
  )
}

export const getDistrictsRequest = async (
  departmentCode,
  municipalityCode,
  token
) => {
  return await request(
    `${BASE_URL}/catalog/districts/${departmentCode}/${municipalityCode}`,
    'GET',
    {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }
  )
}

export const getOfferTypesRequest = async (token) => {
  return await request(`${BASE_URL}/catalog/offer-type`, 'GET', {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  })
}

export const getEconomicActivitiesRequest = async (token) => {
  return await request(`${BASE_URL}/catalog/economic-activity`, 'GET', {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  })
}

export const getDocumentTypeRequest = async (token) => {
  return await request(`${BASE_URL}/catalog/document-type`, 'GET', {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  })
}

export const getMeasurementUnitRequest = async (token) => {
  return await request(`${BASE_URL}/catalog/measurement-unit`, 'GET', {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  })
}

export const getSaleTypeRequest = async (token) => {
  return await request(`${BASE_URL}/catalog/sale-type`, 'GET', {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  })
}

export const getPaymentMethodRequest = async (token) => {
  return await request(`${BASE_URL}/catalog/payment-method`, 'GET', {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  })
}

export const getTermRequest = async (token) => {
  return await request(`${BASE_URL}/catalog/term`, 'GET', {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  })
}

export const getOperationConditionRequest = async (token) => {
  return await request(`${BASE_URL}/catalog/operation-condition`, 'GET', {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  })
}

// Tipo de contribuyente (CAT-091). Los id de catálogo cambian entre
// ambientes, así que hay que resolverlos contra el backend en vez de
// hardcodearlos: en DEV "Gran contribuyente" es 1324 y en QA es 1072
export const getTaxpayerTypeRequest = async (token) => {
  return await request(`${BASE_URL}/catalog/taxpayer-type`, 'GET', {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  })
}

// Tipo de persona (CAT-029). Hoy coincide en DEV y QA, pero el id se resuelve
// igual desde el backend para no depender de que siga coincidiendo
export const getPersonTypeRequest = async (token) => {
  return await request(`${BASE_URL}/catalog/person-type`, 'GET', {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  })
}

/* Clients */

export const getClientsRequest = async (id, token) => {
  return await request(`${BASE_URL}/client/${id ? id : ''}`, 'GET', {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  })
}

export const addClientRequest = async (client, token) => {
  return await request(
    `${BASE_URL}/client`,
    'POST',
    {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    client
  )
}

export const editClientRequest = async (client, token) => {
  const response = await request(
    `${BASE_URL}/client/${client.id}`,
    'PUT',
    {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    client
  )

  return response
}

export const deleteClientRequest = async (clientId, token) => {
  return await request(`${BASE_URL}/client/${clientId}`, 'DELETE', {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  })
}

export const getMHTransactionsRequest = async (dteId, token) => {
  return await request(`${BASE_URL}/transaction/dte/${dteId}/1`, 'GET', {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  })
}

/*----------------------------------------- CRUD Productos ------------------------------------------------*/

export const getProductsRequest = async (token) => {
  return await request(`${BASE_URL}/productService/`, 'GET', {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  })
}

export const createProductsRequest = async (product, token) => {
  return await request(
    `${BASE_URL}/productService/`,
    'POST',
    {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    product
  )
}

export const updateProductRequest = async (product, token) => {
  return await request(
    `${BASE_URL}/productService/${product.id}`,
    'PUT',
    {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    product
  )
}

export const deleteProductRequest = async (productId, token) => {
  return await request(
    `${BASE_URL}/productService/${productId}`,
    'DELETE',
    {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }
  )
}

export const getProductTypesRequest = async (token) => {
  return await request(`${BASE_URL}/catalog/product-type`, 'GET', {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  })
}

export const getDocumentsRequest = async (
  token,
  documentTypeId,
  companyId,
  clientDocumentType,
  documentNumber
) => {
  return await request(
    `${BASE_URL}/transaction/dte/${documentTypeId}/${companyId}?clientType=${clientDocumentType}&clientDocument=${documentNumber}&isSuccessful=1`,
    'GET',
    {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }
  )
}

export const getDocumentProductsRequest = async (
  token,
  companyId,
  generationCode,
  createdAt,
  user,
  catalogs = {}
) => {
  const documentProductList = await request(
    `${BASE_URL}/transaction/json/${companyId}/${generationCode}/${createdAt}`,
    'GET',
    {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...getInternalLogHeader(user?.userName),
    }
  )

  const documentBody = JSON.parse(documentProductList)
  const transformData = transformMHProductsToLocal(
    documentBody.cuerpoDocumento,
    catalogs
  )

  return {
    transformData,
    relatedDoc: {
      ...documentBody.identificacion,
      montoIva: documentBody.resumen?.totalIva,
      // El resumen completo del documento ajustado: sin él la nota no puede
      // saber si el CCFE llevaba percepción o retención (#99, #101)
      resumenOriginal: documentBody.resumen ?? null,
    },
  }
}

export const getDocumentProductsJsonRequest = async (
  token,
  docId,
  catalogs = {}
) => {
  const documentProductList = await request(
    `${BASE_URL}/transaction/file/json/${docId}`,
    'GET',
    {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }
  )

  const documentBody = JSON.parse(documentProductList)
  const transformData = transformMHProductsToLocal(
    documentBody.cuerpoDocumento,
    catalogs
  )

  return {
    transformData,
    relatedDoc: {
      ...documentBody.identificacion,
      montoIva: documentBody.resumen?.totalIva,
      // El resumen completo del documento ajustado: sin él la nota no puede
      // saber si el CCFE llevaba percepción o retención (#99, #101)
      resumenOriginal: documentBody.resumen ?? null,
    },
  }
}

export const getIssuerRequest = async (token, userId) => {
  return await request(`${BASE_URL}/issuer/${userId}`, 'GET', {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  })
}

export const getIssuerConfigRequest = async (token, userId) => {
  return await request(`${BASE_URL}/users/${userId}/config`, 'GET', {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  })
}
